import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BrainCircuit, Lock, Sparkles, Loader2, X, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { LEARNER_QUIZ } from "@/lib/learnerQuiz";

export default function LearnerProfileCard() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const Q = LEARNER_QUIZ[lang] || LEARNER_QUIZ.fr;
  const U = Q.ui;

  const [locked, setLocked] = useState(false);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState(Array(12).fill(0));
  const [o1, setO1] = useState("");
  const [o2, setO2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/learner-profile").then(({ data }) => {
      setLocked(!!data.locked);
      setProfile(data.profile || null);
    }).catch(() => {});
  }, []);

  const setAns = (idx, val) => setAnswers((a) => { const n = [...a]; n[idx] = val; return n; });

  const submit = async () => {
    if (answers.some((v) => v === 0)) { toast.error(U.answerAll); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/learner-profile", { answers, block_trigger: o1, ideal_mentor: o2, lang });
      setProfile(data.profile);
      setOpen(false);
      toast.success("✨");
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      else toast.error(U.error);
    } finally { setBusy(false); }
  };

  const startTest = () => {
    setAnswers(Array(12).fill(0)); setO1(""); setO2(""); setOpen(true);
  };

  let qi = 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
      className="glass rounded-2xl p-6 mb-5" data-testid="profile-learner"
    >
      <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90 mb-1">
        <BrainCircuit size={18} className="text-primary" /> {U.cardTitle}
      </h2>
      <p className="text-sm text-foreground/50 mb-5">{U.cardDesc}</p>

      {locked ? (
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-6 text-center" data-testid="learner-locked">
          <Lock className="mx-auto text-primary mb-3" size={22} />
          <p className="font-display text-lg mb-1">{U.lockedTitle}</p>
          <p className="text-sm text-foreground/55 mb-5 max-w-md mx-auto">{U.lockedDesc}</p>
          <Link to="/tarifs" data-testid="learner-upsell" className="btn-frost-orange px-6 py-2.5 rounded-full font-semibold inline-flex">
            {U.upsell}
          </Link>
        </div>
      ) : profile ? (
        <div data-testid="learner-result">
          <div className="rounded-2xl bg-primary/[0.06] border border-primary/20 p-5 mb-5">
            <p className="uppercase tracking-[0.18em] text-[10px] text-primary/70 mb-2 flex items-center gap-1.5"><Sparkles size={12} /> {U.portraitTitle}</p>
            <p className="font-display italic text-foreground/85 leading-relaxed" data-testid="learner-portrait">{profile.portrait}</p>
          </div>

          {profile.axes && (
            <div className="grid grid-cols-2 gap-3 mb-5">
              {Object.entries(U.axes).map(([k, label]) => (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-foreground/60">{label}</span><span className="text-primary/80 tabular-nums">{profile.axes[k]}%</span></div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-primary/70 rounded-full" style={{ width: `${profile.axes[k]}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {profile.settings && (
            <>
              <p className="text-sm text-primary/80 mb-2">{U.settingsTitle}</p>
              <div className="flex flex-wrap gap-2 mb-5" data-testid="learner-settings">
                {Object.entries(U.settingLabels).map(([k, label]) => profile.settings[k] ? (
                  <span key={k} className="text-xs rounded-full bg-white/[0.04] border border-white/10 px-3 py-1.5">
                    <span className="text-foreground/45">{label} : </span><span className="text-foreground/85">{profile.settings[k]}</span>
                  </span>
                ) : null)}
              </div>
            </>
          )}

          <button onClick={startTest} data-testid="learner-retake" className="text-sm text-foreground/60 hover:text-primary inline-flex items-center gap-2">
            <RefreshCw size={14} /> {U.retake}
          </button>
        </div>
      ) : (
        <button onClick={startTest} data-testid="learner-start" className="btn-frost-orange px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
          <BrainCircuit size={16} /> {U.start}
        </button>
      )}

      {/* Quiz modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => !busy && setOpen(false)} data-testid="learner-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl max-w-2xl w-full my-8 p-6 sm:p-8 relative"
            >
              <button onClick={() => !busy && setOpen(false)} className="absolute top-4 right-4 text-foreground/40 hover:text-foreground" data-testid="learner-close"><X size={20} /></button>
              <h3 className="font-display text-2xl text-gradient-orange mb-1">{U.cardTitle}</h3>
              <p className="text-sm text-foreground/50 mb-6">{U.cardDesc}</p>

              {Q.sections.map((sec, si) => (
                <div key={si} className="mb-6">
                  <p className="uppercase tracking-[0.16em] text-[11px] text-primary/70 mb-3">{sec.title}</p>
                  {sec.qs.map((q) => {
                    const idx = qi++;
                    return (
                      <div key={idx} className="mb-4">
                        <p className="text-sm text-foreground/80 mb-2 leading-snug">{q}</p>
                        <div className="flex gap-1.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <button key={v} onClick={() => setAns(idx, v)} data-testid={`learner-q${idx}-${v}`} title={Q.scale[v - 1]}
                              className={`flex-1 h-9 rounded-lg text-sm font-medium border transition-all ${answers[idx] === v ? "bg-primary/25 border-primary text-primary" : "bg-white/[0.03] border-white/10 text-foreground/40 hover:border-primary/40"}`}>
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div className="mb-4">
                <label className="block text-sm text-foreground/80 mb-2">{Q.open1}</label>
                <textarea value={o1} onChange={(e) => setO1(e.target.value)} rows={2} maxLength={400} placeholder={Q.open1ph} data-testid="learner-open1"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-foreground/90 focus:outline-none focus:border-primary/50 resize-none" />
              </div>
              <div className="mb-6">
                <label className="block text-sm text-foreground/80 mb-2">{Q.open2}</label>
                <textarea value={o2} onChange={(e) => setO2(e.target.value)} rows={2} maxLength={400} placeholder={Q.open2ph} data-testid="learner-open2"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-foreground/90 focus:outline-none focus:border-primary/50 resize-none" />
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => setOpen(false)} disabled={busy} className="px-5 py-3 rounded-full text-foreground/60 hover:text-foreground">{U.cancel}</button>
                <button onClick={submit} disabled={busy} data-testid="learner-submit" className="btn-frost-orange px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> {U.generating}</> : <><Sparkles size={16} /> {U.submit}</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
